import React, { useState } from "react";
import classNames from "classnames";
import { Feature } from "ol";
import { executeQuery } from "../Map/rest";
import { useAppContext } from "../Contexts/AppContext";
import { config } from "../Map/config";
import { CloseButton } from "../Elements/CloseButton";
import { PopupHeader, PopupBody } from "./Popup";
import "./SearchMenu.scss";

const maxResults = 7;
const minLettersToSearch = 4;
const searchConfig = config.search.searchLayers;

export const SearchMenu = (): JSX.Element => {
    const [text, setText] = useState<string>("");
    const [results, setResults] = useState<Feature[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selected, setSelected] = useState<Feature>();

    const { isMobile, toggleSearchVisible, setLocateFeature, setLayerToToggle } = useAppContext();

    const executeSearch = async (searchString: string) => {
        if (!searchString) {
            return;
        }
        setLoading(true);
        const queries = buildQueries(searchString);
        const queryResults = await doQuery(queries);
        setResults(queryResults.slice(0, maxResults));
        setLoading(false);
    };

    const onChange = (e) => {
        setText(e.target.value);
        e.target.value.length > minLettersToSearch && executeSearch(e.target.value);
    };

    const onClear = () => {
        setText("");
        setSelected(undefined);
        setResults([]);
    };

    const onLocateFeature = (feature: Feature) => {
        setLocateFeature(feature);
        setLayerToToggle({ visible: true, layerId: "cadaster" });
    };

    return (
        <div className="shadow" id="search-popup">
            {isMobile ? (
                <PopupHeader isMobile={isMobile}>
                    <CloseButton onClose={toggleSearchVisible}></CloseButton>
                </PopupHeader>
            ) : (
                <div className="triangle-left"></div>
            )}
            <PopupBody isMobile={isMobile}>
                <div className="draw-toolbar search-bar">
                    <div className="search-input">
                        <input type="text" placeholder="Meklēt pēc kadastra" value={text} onChange={onChange} />
                        {loading && (
                            <div className="search-clear">
                                <span className="icon-conexus-spinner rotating"></span>
                            </div>
                        )}
                        {!loading && text && (
                            <div className="search-clear" onClick={onClear}>
                                <span className="icon-conexus-close-popup"></span>
                            </div>
                        )}
                    </div>
                </div>
                <div className={classNames("search-list", { none: selected })}>
                    {results.map((result, i) => (
                        <div className="search-selected" key={"option-" + i} onClick={() => onLocateFeature(result)}>
                            <div className="symbol-label">
                                <span className="icon-conexus-zoom-in"></span>
                            </div>
                            <div>{getValue(result)}</div>
                        </div>
                    ))}
                </div>
            </PopupBody>
        </div>
    );
};

const buildQueries = (searchText: string) =>
    searchConfig.map((searchLayer) => ({
        layerId: searchLayer.layerId,
        params: {
            f: "json",
            where: "UPPER(" + searchLayer.field + ") LIKE '%" + searchText.toUpperCase() + "%'",
            outFields: "*"
        }
    }));

const doQuery = async (queries) => {
    const results = [];
    for (const query of queries) {
        const features = await executeQuery(query.params, query.layerId);
        results.push(...features);
    }
    return results;
};

const getValue = (feature: Feature) => {
    let value;
    for (const searchLayer of config.search.searchLayers) {
        value = feature.get(searchLayer.displayFieldName || searchLayer.field);
        if (value) {
            break;
        }
    }
    return value;
};
